import { useEffect, useMemo, useRef, useState } from 'react';
import { ClipboardPaste, Copy, ExternalLink, Scissors, TextSelect } from 'lucide-react';
import { useDesktopMatterTabs } from './DesktopMatterTabsContext';
import { runTextContextCommand, type TextContextCommand } from './textContextActions';
import './desktopMatterTabs.css';

const MENU_WIDTH = 244;
const VIEWPORT_GUTTER = 8;

export default function DesktopMatterContextMenu() {
  const tabs = useDesktopMatterTabs();
  const menuRef = useRef<HTMLDivElement>(null);
  const itemRef = useRef<HTMLButtonElement>(null);
  const [error, setError] = useState('');
  const menu = tabs.contextMenu;
  const itemCount = menu
    ? Number(Boolean(menu.matter))
      + Number(Boolean(menu.text?.canCut))
      + Number(Boolean(menu.text?.canCopy))
      + Number(Boolean(menu.text?.canPaste))
      + Number(Boolean(menu.text?.canSelectAll))
    : 0;
  const menuHeight = itemCount * 40 + (menu?.matter && menu.text ? 9 : 8);

  const position = useMemo(() => {
    if (!menu || typeof window === 'undefined') return { left: 0, top: 0 };
    return {
      left: Math.max(VIEWPORT_GUTTER, Math.min(menu.clientX, window.innerWidth - MENU_WIDTH - VIEWPORT_GUTTER)),
      top: Math.max(VIEWPORT_GUTTER, Math.min(menu.clientY, window.innerHeight - menuHeight - VIEWPORT_GUTTER)),
    };
  }, [menu, menuHeight]);

  useEffect(() => {
    if (!menu) return;
    itemRef.current?.focus();

    const dismissOnPointer = (event: PointerEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) tabs.dismissContextMenu();
    };
    const dismissOnKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') tabs.dismissContextMenu();
    };
    const dismiss = () => tabs.dismissContextMenu();

    document.addEventListener('pointerdown', dismissOnPointer);
    document.addEventListener('keydown', dismissOnKey);
    window.addEventListener('blur', dismiss);
    window.addEventListener('resize', dismiss);
    return () => {
      document.removeEventListener('pointerdown', dismissOnPointer);
      document.removeEventListener('keydown', dismissOnKey);
      window.removeEventListener('blur', dismiss);
      window.removeEventListener('resize', dismiss);
    };
  }, [menu, tabs]);

  if (!tabs.enabled || !menu) return null;

  const runTextAction = async (command: TextContextCommand) => {
    if (!menu.text) return;
    try {
      setError('');
      await runTextContextCommand(menu.text, command, navigator.clipboard);
      tabs.dismissContextMenu();
    } catch {
      setError('Clipboard access was blocked. Please try again.');
    }
  };

  return (
    <div
      ref={menuRef}
      role="menu"
      aria-label="Valentine context actions"
      className="desktop-matter-context-menu"
      style={position}
    >
      {menu.matter && (
        <button
          ref={itemRef}
          type="button"
          role="menuitem"
          onClick={() => {
            tabs.openMatterInBackground(menu.matter!);
            tabs.dismissContextMenu();
          }}
        >
          <ExternalLink size={15} aria-hidden="true" />
          Open in new Valentine tab
        </button>
      )}
      {menu.matter && menu.text && <div className="desktop-context-menu-separator" role="separator" />}
      {menu.text?.canCut && (
        <button ref={menu.matter ? undefined : itemRef} type="button" role="menuitem" onClick={() => void runTextAction('cut')}>
          <Scissors size={15} aria-hidden="true" /> Cut
        </button>
      )}
      {menu.text?.canCopy && (
        <button ref={!menu.matter && !menu.text.canCut ? itemRef : undefined} type="button" role="menuitem" onClick={() => void runTextAction('copy')}>
          <Copy size={15} aria-hidden="true" /> Copy
        </button>
      )}
      {menu.text?.canPaste && (
        <button ref={!menu.matter && !menu.text.canCut && !menu.text.canCopy ? itemRef : undefined} type="button" role="menuitem" onClick={() => void runTextAction('paste')}>
          <ClipboardPaste size={15} aria-hidden="true" /> Paste
        </button>
      )}
      {menu.text?.canSelectAll && (
        <button type="button" role="menuitem" onClick={() => void runTextAction('selectAll')}>
          <TextSelect size={15} aria-hidden="true" /> Select all
        </button>
      )}
      {error && <div className="desktop-context-menu-error" role="status">{error}</div>}
    </div>
  );
}
